import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import useCompete from '@stores/compete';
import useWindowSize from '@hooks/useWindowSize';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import { forwardRef } from 'react';
import Typography from 'aquilla/core/atoms/Typography';
import Image from '@base/FutureImage';
import { LEADERBOARD_VIEWED } from '@cont/Compete/constants';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import { getNoRatingState } from '@cont/Compete/utils';
import isEmptyObject from '@utils/isEmptyObject';
import Link from 'aquilla/core/molecules/Link';
import useFilter from './useFilter';
import LeaderboardItem from './LeaderboardItem';

const Wrapper = styled.div`
  width: 100%;
  border: 1px solid var(--color-divider);
  border-bottom: ${({ $source }) => $source === 'Modal' && 'none'};
  ${mediaQuery.uptoMobile} {
    margin: ${({ $source }) => ($source === 'Modal' ? 0 : '0 -16px')};
    width: 100vw;
    border: none;
    border-top: 1px solid var(--color-divider);
  }
`;

const Header = styled.div`
  height: 48px;
  border-bottom: 1px solid var(--color-divider);
  padding: ${Spacings.SPACING_4B} 0;
  display: flex;
  position: sticky;
  top: 0;
  background-color: var(--color-base-0);
  ${mediaQuery.uptoMobile} {
    top: 56px;
  }
`;

const List = styled.div``;

const ColumnHeader = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P2};
  line-height: 16px;
  color: var(--color-text-secondary);
`;

const RankHeader = styled(ColumnHeader)`
  width: 70px;
  margin-left: ${Spacings.SPACING_6B};
  margin-right: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoMobile} {
    width: 56px;
    margin-left: ${Spacings.SPACING_2B};
    margin-right: ${Spacings.SPACING_3B};
    text-align: center;
  }
`;

const LearnerHeader = styled(ColumnHeader)`
  width: 400px;
  ${mediaQuery.uptoMobile} {
    width: auto;
  }
`;

const RatingHeader = styled(ColumnHeader)`
  width: 47px;
  margin-right: ${Spacings.SPACING_6B};
  text-align: right;

  ${mediaQuery.uptoMobile} {
    width: 41px;
    margin-right: 0;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  width: calc(100% - 142px);

  ${mediaQuery.uptoMobile} {
    padding-right: ${Spacings.SPACING_4B};
    width: calc(100% - 76px);
  }
`;

const EmptyState = styled.div`
  height: ${({ $source }) => ($source === 'Modal' ? '380px' : '776px')};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledLink = styled(Link)`
  svg {
    width: 24px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  color: var(--color-text-primary);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: 20px;
  border-radius: 8px;
  cursor: pointer;
  border-top: 1px solid var(--color-divider);

  ${mediaQuery.uptoMobile} {
    border: 1px solid var(--color-divider);
    margin: ${Spacings.SPACING_4B};
    height: 44px;
  }
`;

const Block = styled.div`
  width: 100%;
  height: 1000px;
`;

const Leaderboard = forwardRef(
  ({ source, leaderboard = [], setSize, myData = {} }, ref) => {
    const { isMobile } = useWindowSize();
    const { setLeaderboardDialogState } = useCompete();
    const { goalUID } = useRouter();
    const goalSegmentData = useGoalEventProps({ goalUID });
    const meInfo = useMyInfo(getMe);
    const { examFilter, stateFilter } = useCompete();
    const { data: filterData } = useFilter();
    const { userPreference } = filterData;
    const isLoggedIn = useMyInfo(getIsLoggedIn);

    const getProcessedLeaderboard = () => {
      if (myData.rank > 10) {
        const newLeaderboard = leaderboard?.slice(0, 9) ?? [];
        newLeaderboard.push(myData);
        return newLeaderboard;
      }
      if (
        (userPreference.stateCode === stateFilter.code ||
          stateFilter.id === -1) &&
        (examFilter.id === -1 ||
          userPreference.examPreferenceIds?.includes(examFilter.id)) &&
        isEmptyObject(myData) &&
        isLoggedIn
      ) {
        const noRating = getNoRatingState(meInfo);
        const newLeaderboard = leaderboard?.slice(0, 9) ?? [];
        newLeaderboard.push(noRating);
        return newLeaderboard;
      }
      return leaderboard;
    };

    const renderLeaderboardList = () => {
      const processedLeaderboard = getProcessedLeaderboard();

      return processedLeaderboard?.map((item, index) => {
        return (
          <LeaderboardItem
            item={item}
            index={index}
            key={item.id}
            source={source}
          />
        );
      });
    };

    const renderLeaderboardInfiniteList = () => {
      const scrollElement = isMobile
        ? document.getElementsByClassName('open aquilla-drawer')[0]
        : document.getElementsByClassName('aquilla-dialog-content')[0];

      if (scrollElement) {
        return (
          <>
            <InfiniteList
              data={leaderboard}
              loadMore={() => setSize((size) => size + 1)}
              RenderItem={LeaderboardItem}
              loaderColor="var(--color-i-blue)"
              loaderType="progress"
              scrollElement={scrollElement || ''}
              extraProps={{ ref }}
            />
          </>
        );
      }
      return <Block />;
    };

    const renderEmptyState = () => {
      return (
        <EmptyState $source={source}>
          <Image src="/gtp/no-results.svg" width={80} height={80} alt="" />
          <EmptyText>
            <Typography variant="h4">No results found</Typography>
            <Typography variant="p2" color="var(--color-text-secondary)">
              Try changing the filters applied
            </Typography>
          </EmptyText>
        </EmptyState>
      );
    };

    const renderLeaderboardBasedOnSource = () => {
      if (
        (!leaderboard?.length && source !== 'Modal') ||
        (!leaderboard?.results?.length && source === 'Modal')
      ) {
        return renderEmptyState();
      }
      if (source === 'Modal') {
        return renderLeaderboardInfiniteList();
      }
      return renderLeaderboardList();
    };

    const renderLeaderboard = () => {
      return (
        <>
          <Header>
            <RankHeader>{isMobile ? 'Rank' : 'Current rank'}</RankHeader>
            <Flex>
              <LearnerHeader>Learners</LearnerHeader>
              <RatingHeader>Rating</RatingHeader>
            </Flex>
          </Header>
          <List>{renderLeaderboardBasedOnSource()}</List>
        </>
      );
    };

    const handleFullLeaderboardClick = () => {
      setLeaderboardDialogState(true);
      segment.track(LEADERBOARD_VIEWED, {
        ...goalSegmentData,
        filter_type: 'Full Leaderboard',
        filter_value: 'Full Leaderboard'
      });
    };
    return (
      <Wrapper $source={source}>
        {renderLeaderboard()}
        {source === 'Home' && leaderboard?.length >= 10 && (
          <StyledLink
            rightArrow
            underline={false}
            onClick={(e) => {
              e.preventDefault();
              handleFullLeaderboardClick();
            }}
            href="/"
          >
            <Typography variant="h6" color="var(--color-text-primary)">
              View full leaderboard
            </Typography>
          </StyledLink>
        )}
      </Wrapper>
    );
  }
);

Leaderboard.displayName = 'Leaderboard';

export default Leaderboard;
